/**
 * Lead Drop Popup 5002 — BDU-style single-step lead form.
 *
 * Flow:  ONE screen (Name / Phone / Email / Course / Qualification) where the OTP
 *        box is revealed *inline* as soon as name+phone+email validate and the OTP
 *        has been dispatched  →  last card (Registered / Welcome Back / Download /
 *        Login states).
 *
 * Unlike popup 9 there is no separate OTP step: the user never leaves the form.
 * Layout is the two-column BDU card (form left, tall image right; image on top at
 * mobile/tablet) over a configurable horizontal gradient. The inline twin
 * (`LEAD_DROP_FORM_5002`) reuses the same field set through `leadDropForm_5002.ts`.
 *
 * Defaults reproduce Bharathidasan University's current form verbatim, so BDU
 * renders unchanged out of the box while any other tenant can restyle it entirely
 * from the admin panel.
 */

/**
 * Real `lead_drop_popup` columns. Anything NOT listed here must be flagged
 * `isJsonField` so Form_4 nests it under `additional_fields` — Sequelize silently
 * drops unknown top-level keys, which would lose the value on save.
 */
const LEAD_DROP_POPUP_COLUMNS = new Set([
  "form_title",
  "form_title_color",
  "form_sub_text",
  "form_sub_text_color",
  "checkbox_description",
  "checkbox_description_color",
  "checkbox_color",
  "terms_conditions_link",
  "privacy_policy_link",
  "new_user_title",
  "new_user_title_color",
  "new_user_description",
  "new_user_description_color",
  "new_user_desktop_image",
  "new_user_image_alt",
  "existing_user_title",
  "existing_user_title_color",
  "existing_user_description",
  "existing_user_description_color",
  "existing_user_desktop_image",
  "existing_user_image_alt",
  "brochure_title",
  "brochure_title_color",
  "brochure_description",
  "brochure_description_color",
  "brochure_link_color",
  "brochure_desktop_image",
  "brochure_image_alt",
  "popup_display_interval_1",
  "popup_display_interval_2",
  "popup_display_interval_3",
  "enable_delay_form",
  "enable_exit_form",
]);

type FieldConfig = {
  fieldName: string;
  label: string;
  value: string | boolean;
  valueType: "string" | "boolean";
  filedType: "INPUT" | "TEXT_AREA" | "COLOR" | "FILE" | "CHECKBOX" | "NUMBER";
  placeholder: string;
  required: boolean;
  defaultValue: string | boolean;
  isJsonField?: boolean;
  rows?: number;
  description?: string;
  validation?: (
    value: any,
    formData: Record<string, any>,
  ) => string | null;
};

/**
 * Builds one field entry, deriving `isJsonField` from the column list above so the
 * column-vs-JSON decision is made in exactly one place.
 */
const mk = (
  fieldName: string,
  label: string,
  defaultValue: string | boolean,
  filedType: FieldConfig["filedType"],
  extra: Partial<FieldConfig> = {},
): FieldConfig => ({
  fieldName,
  label,
  value: defaultValue,
  valueType: typeof defaultValue === "boolean" ? "boolean" : "string",
  filedType,
  placeholder:
    extra.placeholder ??
    (filedType === "COLOR"
      ? `Enter ${label} (e.g., ${defaultValue || "#FFFFFF"})`
      : filedType === "FILE"
        ? `Upload ${label}`
        : `Enter ${label}`),
  required: false,
  defaultValue,
  ...(LEAD_DROP_POPUP_COLUMNS.has(fieldName) ? {} : { isJsonField: true }),
  ...extra,
});

const text = (n: string, l: string, d: string, e?: Partial<FieldConfig>) =>
  mk(n, l, d, "INPUT", e);
const area = (n: string, l: string, d: string, rows = 2, e?: Partial<FieldConfig>) =>
  mk(n, l, d, "TEXT_AREA", { rows, ...e });
const color = (n: string, l: string, d: string, e?: Partial<FieldConfig>) =>
  mk(n, l, d, "COLOR", e);
const file = (n: string, l: string, d = "", e?: Partial<FieldConfig>) =>
  mk(n, l, d, "FILE", e);
const toggle = (n: string, l: string, d = false, e?: Partial<FieldConfig>) =>
  mk(n, l, d, "CHECKBOX", e);

const LEAD_DROP_POPUP_5002_FIELDS: FieldConfig[] = [
  // ── Header ──────────────────────────────────────────────────────────────────
  text("form_title", "Form Title", "Applications closing soon."),
  color("form_title_color", "Form Title Color", "#FFFFFF"),
  text("form_sub_text", "Form Sub Text", "Enroll Now, Limited Seats Left!"),
  color("form_sub_text_color", "Form Sub Text Color", "#FFFFFF"),

  // ── Card appearance ─────────────────────────────────────────────────────────
  color(
    "form_background_gradient_from",
    "Card Gradient — Start Color",
    "#13344F",
  ),
  color("form_background_gradient_to", "Card Gradient — End Color", "#77B6EB"),
  // Uploaded through the CMS — no bundled default. Left empty the popup renders
  // as a single full-width form column.
  file("side_image", "Side Image (right on desktop, top on mobile)"),
  text("side_image_alt", "Side Image Alt Text", ""),
  // BDU tints the side image with `bg-black-300`. A plain text field, not a
  // COLOR picker, because the value carries alpha (8-digit hex / rgba) and the
  // picker only stores 6-digit hex. Set to "transparent" for an untinted photo.
  text("side_image_overlay_color", "Side Image Overlay Tint", "#00000059", {
    placeholder: "#00000059 / rgba(0,0,0,0.35) / transparent",
  }),
  file("close_icon", "Close Icon (leave empty for the default cross)"),
  text("close_icon_alt", "Close Icon Alt Text", "Close"),

  // ── Field labels & placeholders ─────────────────────────────────────────────
  text("name_label", "Name — Label", "Enter Name"),
  text("name_placeholder", "Name — Placeholder", "Enter Name"),
  text("phone_label", "Phone — Label", "Phone Number"),
  text("phone_placeholder", "Phone — Placeholder", "Enter your phone number"),
  text("email_label", "Email — Label", "Email ID"),
  text("email_placeholder", "Email — Placeholder", "Enter your Email ID *"),
  text("course_label", "Course — Label", "Select Course"),
  text("course_placeholder", "Course — Placeholder", "Select your course"),
  text("qualification_label", "Qualification — Label", "Select Highest Qualification"),
  text(
    "qualification_placeholder",
    "Qualification — Placeholder",
    "Select Highest Qualification",
  ),
  color("field_label_color", "Field Label Color", "#FFFFFF"),
  color("required_marker_color", "Required Asterisk Color", "#FECACA"),
  color("error_text_color", "Validation Error Text Color", "#EF4444"),

  // ── Inline OTP ──────────────────────────────────────────────────────────────
  // Shown in-place once name/phone/email validate — this variant has no separate
  // OTP screen.
  text("otp_label", "OTP — Label", "Enter OTP"),
  text("otp_placeholder", "OTP — Placeholder", "Enter OTP *"),
  text("resend_prefix_text", "Resend Countdown Prefix", "Resend in", {
    description: 'Rendered as "<prefix> 30" while the countdown runs.',
  }),
  text("resend_otp_text", "Resend OTP Text", "Resend OTP"),
  color("resend_otp_text_color", "Resend OTP Text Color", "#F4F217"),
  mk("resend_countdown_seconds", "Resend Countdown (seconds)", "30", "INPUT", {
    placeholder: "Enter countdown length in seconds (e.g., 30)",
    description: "How long before 'Resend OTP' becomes clickable. Defaults to 30.",
  }),

  // ── Submit button ───────────────────────────────────────────────────────────
  text("button_text", "Submit Button Text", "Enroll Now"),
  color("button_text_color", "Submit Button Text Color", "#FFFFFF"),
  // BDU paints Enroll Now with a red gradient (bg-button-gradient-red). Clear
  // either stop to fall back to the solid Background Color below.
  color("button_gradient_color_1", "Submit Button Gradient — Start Color", "#FF0013"),
  color("button_gradient_color_2", "Submit Button Gradient — End Color", "#990013"),
  text("button_gradient_direction", "Submit Button Gradient Direction", "to right", {
    placeholder: "to right / to bottom / 90deg",
  }),
  color("button_background_color", "Submit Button Background Color (fallback, used only if a gradient stop is empty)", "#13344F"),

  // ── Consent ─────────────────────────────────────────────────────────────────
  area(
    "checkbox_description",
    "Consent Text",
    "By clicking on submit I allow Bharathidasan University to send program communication on email/sms/WhatsApp etc.",
    3,
  ),
  text("terms_conditions_link", "Terms & Conditions Link", "", {
    placeholder: "https://example.com/terms-and-conditions",
    description:
      "Destination URL for link slot 1. Referenced in Consent Text as {{1:Your link text}}.",
  }),
  text("privacy_policy_link", "Privacy Policy Link", "", {
    placeholder: "https://example.com/privacy-policy",
    description:
      "Destination URL for link slot 2. Referenced in Consent Text as {{2:Your link text}}.",
  }),
  color("checkbox_description_color", "Consent Text Color", "#FFFFFF"),
  color("checkbox_color", "Checkbox / Consent Link Color", "#F4F217"),

  // ── Last cards — shared ─────────────────────────────────────────────────────
  color(
    "success_circle_color",
    "Last Card — Decorative Circle Color",
    "#DFF7FD",
  ),

  // ── Last card — Registered (new user) ───────────────────────────────────────
  text("new_user_title", "New User — Title", "Registered successfully!"),
  color("new_user_title_color", "New User — Title Color", "#101112"),
  area(
    "new_user_description",
    "New User — Description",
    "Thank you for showing interest!\nAn expert from Admission counselling team will reach out to you.",
    3,
  ),
  color("new_user_description_color", "New User — Description Color", "#4A5568"),
  file("new_user_desktop_image", "New User — Image"),
  text("new_user_image_alt", "New User — Image Alt Text", "Registered successfully"),

  // ── Last card — Welcome Back (existing user) ────────────────────────────────
  text("existing_user_title", "Existing User — Title", "Welcome Back"),
  color("existing_user_title_color", "Existing User — Title Color", "#101112"),
  area(
    "existing_user_description",
    "Existing User — Description",
    "Our Academic Experts will call you shortly",
    2,
  ),
  color(
    "existing_user_description_color",
    "Existing User — Description Color",
    "#4A5568",
  ),
  file("existing_user_desktop_image", "Existing User — Image"),
  text("existing_user_image_alt", "Existing User — Image Alt Text", "Welcome back"),

  // ── Last card — Login states ────────────────────────────────────────────────
  // Used when a CTA opens the popup with the LOGIN variant.
  text("login_new_title", "Login — New User Title", "Welcome"),
  area(
    "login_new_description",
    "Login — New User Description",
    "You have Successfully Registered",
    2,
  ),
  text("login_existing_title", "Login — Returning User Title", "Welcome back!"),
  area(
    "login_existing_description",
    "Login — Returning User Description",
    "Let’s continue your journey.",
    2,
  ),
  color("login_title_color", "Login — Title Color", "#101112"),
  color("login_description_color", "Login — Description Color", "#4A5568"),

  // ── Last card — Download (brochure) ─────────────────────────────────────────
  text("brochure_title", "Download — Title", "Download successful"),
  color("brochure_title_color", "Download — Title Color", "#101112"),
  area(
    "brochure_description",
    "Download — Description (wrap any text in {{ }} to turn it into the manual download link)",
    "Your download will begin automatically. You can {{click here}} to download it manually..",
    3,
  ),
  color("brochure_description_color", "Download — Description Color", "#101112"),
  color(
    "brochure_link_color",
    "Download — Link Color (applied to the {{ }} text)",
    "#2BADF8",
  ),
  file("brochure_desktop_image", "Download — Image"),
  text("brochure_image_alt", "Download — Image Alt Text", "Download successful"),

  // ── Popup display ───────────────────────────────────────────────────────────
  text("popup_display_interval_1", "Popup Display Interval 1", "", {
    placeholder: "Enter Popup Display Interval 1 (e.g., 3000 - for 3 seconds)",
    validation: (value: any, formData: Record<string, any>) => {
      if (
        formData?.enable_delay_form &&
        (!value || value.toString().trim() === "")
      ) {
        return "Popup Display Interval 1 is required when Enable Delay Form is checked";
      }
      return null;
    },
  }),
  text("popup_display_interval_2", "Popup Display Interval 2", "", {
    placeholder: "Enter Popup Display Interval 2 (e.g., 6000 - for 6 seconds)",
  }),
  text("popup_display_interval_3", "Popup Display Interval 3", "", {
    placeholder: "Enter Popup Display Interval 3 (e.g., 12000 - for 12 seconds)",
  }),
  toggle("enable_delay_form", "Enable Delay Form", false, {
    placeholder: "Enable form to show after delay",
  }),
  toggle("enable_exit_form", "Enable Exit Form", false, {
    placeholder: "Enable form to show on exit intent",
  }),
];

/** `DEFAULT_ROW` is derived from the field list so the two can never drift apart. */
const DEFAULT_ROW = LEAD_DROP_POPUP_5002_FIELDS.reduce<Record<string, unknown>>(
  (row, field) => {
    row[field.fieldName] = field.defaultValue;
    return row;
  },
  {},
);

export const LEAD_DROP_POPUP_5002_CONSTANTS = {
  DEFAULT_ROW,
  LEAD_DROP_POPUP_FIELDS_CONFIG: LEAD_DROP_POPUP_5002_FIELDS,
  METADATA_FIELDS: [],
};
